'use client'

import BookImage from '@components/BookImage'
import { TopNavBar } from '@components/FlowHeader'
import { BookDetailsPageResponse, ExtendedListing, SellersToBookResponse, ShippingPrice } from '@hermes/types'
import { BookCard, Box, InfiniteList } from '@hermes/web-components'
import useTranslateMessage from '@hooks/useTranslateMessage'
import { useCheckoutStore } from '@lib/stores/checkout'

import { useUser } from '@lib/contexts/userContext'

import useChooseDeliveryService from '@hooks/useChooseDeliveryService'
import { useNavigation } from '@hooks/useNavigation'

import { SellerListingCard } from '@components/SellerListingCard'
import SellerListingSkeleton from '@components/SellerListingCardSkeleton'
import useSortAndFilterBar from '@components/useSortAndFilterBar'
import { useRecommendedSellersV2 } from '@hooks/useRecommendedSellerV2'
import { useSearchParams } from 'next/navigation'
import { useAnalytics } from '@lib/contexts/analyticsContext'
import { useIntercom } from '@hooks/useIntercom'
import NoSellersFound from './NoSellersFound'
import {
  BodyContainer,
  GrowNoGrowBox,
  ListContainer,
  SkeletonContainer,
  SkeletonListContainer,
  SubHeaderContainer
} from './ProductSellerPage.styles'

interface ProductSellersPageProps {
  bookDetails: BookDetailsPageResponse['data']
  shippingPrice: ShippingPrice
}

export const ProductSellersPage = ({ bookDetails, shippingPrice }: ProductSellersPageProps) => {
  // TODO: Component needs refactoring! Had to wiggle it to make it work with deliveryMethods
  const { t } = useTranslateMessage()
  const { addToCart } = useCheckoutStore()
  const { clearCart } = useCheckoutStore()
  const setShippingMethod = useCheckoutStore((state) => state.setShippingMethod)
  const params = useSearchParams()
  const { user } = useUser()
  const { navigate } = useNavigation()
  const analytics = useAnalytics()
  const deliveryMethodQuery = (params?.get('deliveryMethod') as 'shipping' | 'meetup') || 'shipping'
  const {
    data: groupedSellerListings,
    isLoading: listingsLoading,
    error,
    hasMore,
    nextPage: loadMoreListings,
    setFilters
  } = useRecommendedSellersV2({
    size: 10,
    bookUuid: bookDetails.uuid,
    deliveryMethods: deliveryMethodQuery === 'shipping' ? ['shipping', 'both'] : [deliveryMethodQuery]
  })

  const { SortAndFilterBar, filterData } = useSortAndFilterBar({
    params,
    onChange: () => {
      const activeShippingMethods = filterData.DELIVERY_METHOD.options
        .filter(({ active }) => active)
        .map((option) => option.value)
      const activeConditions = filterData.CONDITION.options.filter(({ active }) => active).map((option) => option.value)
      setFilters({
        deliveryMethods: activeShippingMethods,
        conditions: activeConditions
      })
    }
  })

  const { ChooseDeliveryServiceModal, setIsOpen: setChooseDeliveryIsOpen } = useChooseDeliveryService({
    shippingPrice,
    onCloseFn: (recommendedSellerListing) => {
      if (!recommendedSellerListing || !recommendedSellerListing.seller.listings) {
        return
      }
      const sellerUuid = recommendedSellerListing.seller.uuid
      if (recommendedSellerListing?.seller?.listings && recommendedSellerListing.seller.listings.length > 1) {
        navigate.moreFromSellerPage(sellerUuid)
      } else {
        navigate.checkoutPage(sellerUuid)
      }
    }
  })

  const activeShippingMethods = filterData.DELIVERY_METHOD.options
    .filter(({ active }) => active)
    .map((option) => option.value)

  const isShipping = activeShippingMethods.includes('shipping')

  const isMeetup = activeShippingMethods.includes('meetup')

  const noResultsFound = !listingsLoading && (error || groupedSellerListings[activeShippingMethods[0]].length === 0)

  const firstRecommendedIndex = groupedSellerListings?.all?.findIndex(({ badges }) => badges?.recommended === true)

  const addToCartAction = (listing: ExtendedListing) => {
    clearCart()
    addToCart(listing)
    const listingShippingMethod = listing.deliveryMethod === 'both' ? 'shipping' : listing.deliveryMethod
    setShippingMethod(listingShippingMethod)
    analytics.trackEvent('PRODUCT_ADDED_TO_CART', { productId: listing.id })
  }

  const handleSellerSelected = (d: SellersToBookResponse['data'][0]) => {
    setChooseDeliveryIsOpen(d)
    addToCartAction(d)
  }

  const goBackToBookPage = () => {
    const bookSlug = bookDetails.slug
    navigate.productPage(bookSlug)
  }

  useIntercom({ hideLauncher: true })

  return (
    <Box>
      <TopNavBar title={t({ id: 'chooseSeller' })} onClick={() => goBackToBookPage()}>
        <SubHeaderContainer>
          <BodyContainer>
            <BookCard
              title={bookDetails.title}
              image={
                <BookImage
                  src={bookDetails.smallImage || bookDetails.fullImage}
                  width={40}
                  height={56}
                  alt={bookDetails.title}
                />
              }
              cover={t({ id: bookDetails.format })}
              year={String(bookDetails.year)}
              author={bookDetails.authors[0]?.fullName || ''}
              inline
            />
          </BodyContainer>
        </SubHeaderContainer>
      </TopNavBar>
      <BodyContainer>
        <Box>
          <SortAndFilterBar />

          {noResultsFound ? (
            <Box>
              <NoSellersFound />
            </Box>
          ) : (
            <>
              <ListContainer>
                <InfiniteList
                  preloader={
                    <SkeletonListContainer
                      sx={{
                        width: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center'
                      }}
                    >
                      {Array(9)
                        .fill(1)
                        .map((_, i) => (
                          <SkeletonContainer key={i}>
                            <SellerListingSkeleton isHighlighted={false && i === 0 && isShipping} />
                          </SkeletonContainer>
                        ))}
                    </SkeletonListContainer>
                  }
                  isLoading={listingsLoading}
                  loadMore={loadMoreListings}
                  hasMore={hasMore}
                >
                  {isShipping &&
                    groupedSellerListings.shipping &&
                    groupedSellerListings.shipping.map((listing, i) => {
                      const isRecommendedSeller =
                        i === firstRecommendedIndex && listing.badges?.recommended && isShipping
                      return (
                        <GrowNoGrowBox key={`listing-${listing.uuid}`} grow={isRecommendedSeller ?? false}>
                          <SellerListingCard
                            data={listing}
                            viewOptions={{
                              isRecommended: isRecommendedSeller,
                              showPriceInCta: true,
                              showSellerLocation: !isShipping,
                              showTotalPrice: isShipping
                            }}
                            onClick={handleSellerSelected}
                            loggedInUserIsSeller={user?.guid === listing?.seller.uuid}
                          />
                        </GrowNoGrowBox>
                      )
                    })}
                  {isMeetup &&
                    groupedSellerListings.meetup &&
                    groupedSellerListings.meetup.map((listing, i) => {
                      const isRecommendedSeller =
                        i === firstRecommendedIndex && listing.badges?.recommended && isShipping
                      return (
                        <GrowNoGrowBox key={`listing-${listing.uuid}`} grow={isRecommendedSeller ?? false}>
                          <SellerListingCard
                            data={listing}
                            viewOptions={{
                              isRecommended: isRecommendedSeller,
                              showPriceInCta: true,
                              showSellerLocation: !isShipping,
                              showTotalPrice: isShipping
                            }}
                            onClick={handleSellerSelected}
                            loggedInUserIsSeller={user?.guid === listing?.seller.uuid}
                          />
                        </GrowNoGrowBox>
                      )
                    })}
                </InfiniteList>
              </ListContainer>
              <ChooseDeliveryServiceModal />
            </>
          )}
        </Box>
      </BodyContainer>
    </Box>
  )
}
